// PRD 08 §3.2 — Relationship Status panel. Anchored to the bottom of the right
// card; rendered only when a code row is selected.
// Conditional branching (PRD):
//   IF linked data exists → list serial codes + names by section.
//   IF nothing linked    → show the guide text steering users to PRO Item Design.

import { useTranslation } from 'react-i18next';
import { GitBranch, Info, LayoutGrid, Link as LinkIcon } from 'lucide-react';

import { useDiseaseCodeRefsQuery } from '../hooks/use-disease-code-refs';
import type { DiseaseCode, DiseaseCodeReferenceItem } from '../types/disease-code.types';

interface DiseaseCodeReferencePanelProps {
  code: DiseaseCode;
}

const SECTION_HEAD_STYLE: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--muted-foreground)',
  fontWeight: 600,
  marginBottom: 6,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

const CHIP_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 'var(--font-size-xs)',
  padding: '4px 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--background)',
  margin: '0 4px 4px 0',
  whiteSpace: 'nowrap',
};

const EMPTY_LINE_STYLE: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--muted-foreground)',
  padding: '8px 0',
};

function Chip({ item }: { item: DiseaseCodeReferenceItem }) {
  return (
    <span style={CHIP_STYLE}>
      <span style={{ color: 'var(--muted-foreground)', fontWeight: 400 }}>{item.serial}</span>
      {' '}
      {item.name}
    </span>
  );
}

export function DiseaseCodeReferencePanel({ code }: DiseaseCodeReferencePanelProps) {
  const { t } = useTranslation('admin');
  const { data, isLoading, error } = useDiseaseCodeRefsQuery(code.id);

  const proItems = data?.proItems ?? [];
  const modules = data?.deploymentModules ?? [];
  const totallyEmpty = !isLoading && !error && proItems.length === 0 && modules.length === 0;

  return (
    <div
      style={{
        borderTop: '1px solid var(--border)',
        padding: 16,
        background: '#FAFBFC',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <LinkIcon size={14} />
        {t('diseaseCodeManagement.referencePanel.title', {
          code: code.code,
          name: code.name,
        })}
      </div>

      {error ? (
        <div style={{ ...EMPTY_LINE_STYLE, color: 'var(--destructive)' }}>
          {t('diseaseCodeManagement.toasts.loadRefsError')}
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <div style={SECTION_HEAD_STYLE}>
              <GitBranch size={12} />
              {t('diseaseCodeManagement.referencePanel.proItems', { count: proItems.length })}
            </div>
            {isLoading ? (
              <div style={EMPTY_LINE_STYLE}>…</div>
            ) : proItems.length > 0 ? (
              proItems.map((item) => <Chip key={item.serial} item={item} />)
            ) : (
              <div style={EMPTY_LINE_STYLE}>
                {t('diseaseCodeManagement.referencePanel.emptyItems')}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={SECTION_HEAD_STYLE}>
              <LayoutGrid size={12} />
              {t('diseaseCodeManagement.referencePanel.modules', { count: modules.length })}
            </div>
            {isLoading ? (
              <div style={EMPTY_LINE_STYLE}>…</div>
            ) : modules.length > 0 ? (
              modules.map((item) => <Chip key={item.serial} item={item} />)
            ) : (
              <div style={EMPTY_LINE_STYLE}>
                {t('diseaseCodeManagement.referencePanel.emptyModules')}
              </div>
            )}
          </div>

          {totallyEmpty ? (
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--muted-foreground)',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Info size={12} style={{ flexShrink: 0 }} />
              {t('diseaseCodeManagement.referencePanel.guideEmpty')}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
